import { useCallback, useMemo } from "react";

import { useAttachmentPolicy } from "~/entities/chat";
import { useGenerationSettings } from "~/entities/model";
import { useChatDropZone, type UseChatDropZoneResult } from "~/features/upload-attachment/useChatDropZone";
import { useUploadAttachment } from "~/features/upload-attachment/useUploadAttachment";
import { getModelById } from "~/shared/data/chat-models";
import { useChatSettingsStore } from "~/shared/store/useChatSettingsStore";
import {
    attachPoolView,
    computeKindLimits,
    countByKind,
    splitImageFiles,
} from "~/widgets/chat-prompt/attachment-helpers";

// Drag-and-drop файлов в окно чата. Хук читает settings/uploads тех же
// сторов, что PromptBox — двойной вызов хуков OK, состояние одно
// (zustand + react-query). Toast'ы при ошибках лимита/режима/mime/size.
// Лимиты/счётчики считаем через те же чистые хелперы, что и PromptBox
// (`computeKindLimits` / `countByKind`), чтобы не плодить дубль derivation.
export function useChatShellDropZone(chatId: string | undefined): UseChatDropZoneResult {
    const settingsQuery = useGenerationSettings();
    const modelId = useChatSettingsStore((s) => s.modelId);
    const mode = useChatSettingsStore((s) => s.mode);
    const model = getModelById(settingsQuery.data, modelId);
    const currentMode = useMemo(() => model?.modes.find((m) => m.name === mode) ?? null, [model, mode]);

    const { items: pendingItems, upload } = useUploadAttachment(chatId ?? null);

    // Перетаскивание адресует тот же общий приёмник, что скрепка: у режима без
    // кадрового канала картинка уходит в референсы, поэтому и меряться она должна
    // их лимитом. Иначе кадровый ноль отбивал бы жест тостом «режим не принимает
    // такой файл», хотя колонка «Референсы» рядом эту картинку ждёт.
    const maxReferences = currentMode?.maxReferenceImages ?? 0;
    const imagesGoToReferences = currentMode?.maxInputImages === 0 && maxReferences > 0;
    const frameItems = useMemo(() => pendingItems.filter((i) => i.channel === undefined), [pendingItems]);
    const referenceItems = useMemo(
        () => pendingItems.filter((i) => i.channel === "reference"),
        [pendingItems],
    );
    const pool = useMemo(
        () =>
            attachPoolView({
                kindLimits: computeKindLimits(currentMode),
                kindCounts: countByKind(frameItems),
                frameCount: frameItems.length,
                referenceCount: referenceItems.length,
                maxReferences,
                imagesGoToReferences,
            }),
        [currentMode, frameItems, referenceItems.length, maxReferences, imagesGoToReferences],
    );

    // Смесь файлов делится по адресатам: картинки — в канал референсов, прочее
    // (видео, аудио) остаётся в общей ленте, поэтому загрузок две.
    const uploadRouted = useCallback<typeof upload>(
        async (files, seedPatch) => {
            if (!imagesGoToReferences) return upload(files, seedPatch);
            const { images, rest } = splitImageFiles(files);
            const created: Awaited<ReturnType<typeof upload>> = [];
            if (images.length > 0) {
                created.push(...(await upload(images, { ...seedPatch, channel: "reference" })));
            }
            if (rest.length > 0) created.push(...(await upload(rest, seedPatch)));
            return created;
        },
        [imagesGoToReferences, upload],
    );

    // Backend AttachmentPolicy — mimeTypes / maxSizeMb для каждого kind.
    // Если ещё не загрузилась (typically initial page load) — null,
    // validateAndSliceFiles тогда пропускает size/mime валидацию (бэк
    // всё равно перепроверит на presign / PUT).
    const policyQuery = useAttachmentPolicy();
    const policy = useMemo(() => {
        const p = policyQuery.data?.supportedTypes;
        if (!p) return null;
        return {
            image: p.image,
            video: p.video,
            audio: p.audio,
        };
    }, [policyQuery.data]);

    return useChatDropZone({
        chatId: chatId ?? null,
        limits: pool.limits,
        currentCounts: pool.counts,
        policy,
        upload: uploadRouted,
    });
}
